// canyonRouter.ts — Main-thread entry for the client-side canyon
// auto-router.
//
// Architecture:
//   1. Compute the bbox covering origin + destination + corridor.
//   2. Warm the Terrarium DEM tile cache via `preloadTerrainBbox()`.
//   3. Build a packed Float32Array elevation grid for the bbox.
//   4. Spawn a Web Worker (`canyonRouter.worker.ts`) and post the
//      grid + request as a transferable.
//   5. Wait for the worker's response and return it.
//
// Boss request 2026-05-19 PT — terrain-aware canyon router for
// helicopter ops, e.g. O46 → 36S via the Scott River → Klamath River
// valley. The existing server-side `/api/route/pass-finder` could not
// see the valley because elevations were sampled too coarsely
// (5 NM corridor x 1 NM lateral via OpenTopoData). This client-side
// path uses the in-memory Terrarium tiles already loaded for the
// Terrain Advisor overlay, so we can run a much denser A* (0.25 NM
// refine grid) without burning API budget or hitting Vercel's 30s
// function ceiling.

import {
  preloadTerrainBbox,
  buildElevationGrid,
} from './terrainSampler';
import type {
  CanyonRouterRequest,
  CanyonRouterResponse,
  CanyonRouterError,
} from './canyonRouter.worker';

export type {
  CanyonRouterRequest,
  CanyonRouterResponse,
  CanyonRouterError,
  CanyonRouterWaypoint,
  CanyonRouterDiagnostics,
} from './canyonRouter.worker';

export interface FindCanyonRouteInput {
  origin: { ident?: string; lat: number; lon: number };
  destination: { ident?: string; lat: number; lon: number };
  cruiseAltFt: number;
  clearanceFt?: number;
  corridorHalfNm?: number;
  coarseStepNm?: number;
  refineStepNm?: number;
  refineBandHalfNm?: number;
  /** Optional abort signal for cancelling preload + worker. */
  signal?: AbortSignal;
  /** Optional progress callback ('preload' | 'compute'). */
  onProgress?: (phase: 'preload' | 'compute', detail?: string) => void;
}

const DEFAULT_CORRIDOR_HALF_NM = 15;     // 30 NM total — wide enough for canyons
const BBOX_PAD_NM = 2;                    // small pad so edge sampling doesn't fall off-grid
const GRID_STEP_NM = 0.2;                 // elevation-grid resolution (≈ Terrarium z=11)

/**
 * Compute the lat/lon bbox covering the great-circle from A to B
 * plus a perpendicular corridor half-width.
 */
function corridorBbox(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
  halfWidthNm: number,
): { minLat: number; minLon: number; maxLat: number; maxLon: number } {
  // Approximate: take origin/destination and pad by (halfWidth + a bit)
  // in both lat and lon. Conservative — over-fetches a few tiles but
  // is far simpler than walking the great circle.
  const padNm = halfWidthNm + BBOX_PAD_NM;
  const midLat = (a.lat + b.lat) / 2;
  const degLatPerNm = 1 / 60;
  const degLonPerNm = 1 / 60 / Math.max(0.01, Math.cos((midLat * Math.PI) / 180));
  const minLat = Math.min(a.lat, b.lat) - padNm * degLatPerNm;
  const maxLat = Math.max(a.lat, b.lat) + padNm * degLatPerNm;
  const minLon = Math.min(a.lon, b.lon) - padNm * degLonPerNm;
  const maxLon = Math.max(a.lon, b.lon) + padNm * degLonPerNm;
  return { minLat, minLon, maxLat, maxLon };
}

/**
 * Singleton worker. Lazily created on first call; reused for
 * subsequent routes. Disposed only if a previous compute is aborted.
 */
let _worker: Worker | null = null;
let _workerInFlight: ((m: CanyonRouterResponse | CanyonRouterError) => void) | null = null;

function getWorker(): Worker {
  if (_worker) return _worker;
  // Vite worker import: `?worker` query → new Worker(url, { type: 'module' }).
  // Falls back to a plain string URL if the bundler doesn't support
  // the ?worker syntax (vitest/node environments).
  // We rely on Vite at build time.
  _worker = new Worker(new URL('./canyonRouter.worker.ts', import.meta.url), { type: 'module' });
  _worker.onmessage = (e: MessageEvent<CanyonRouterResponse | CanyonRouterError>) => {
    const cb = _workerInFlight;
    _workerInFlight = null;
    if (cb) cb(e.data);
  };
  _worker.onerror = (ev) => {
    const cb = _workerInFlight;
    _workerInFlight = null;
    if (cb) cb({ ok: false, error: `worker error: ${ev.message ?? 'unknown'}` });
  };
  return _worker;
}

export async function findCanyonRoute(
  input: FindCanyonRouteInput,
): Promise<CanyonRouterResponse | CanyonRouterError> {
  const {
    origin, destination, cruiseAltFt,
    clearanceFt = 500,
    corridorHalfNm = DEFAULT_CORRIDOR_HALF_NM,
    coarseStepNm,
    refineStepNm,
    refineBandHalfNm,
    signal,
    onProgress,
  } = input;

  if (!Number.isFinite(origin.lat) || !Number.isFinite(origin.lon) ||
      !Number.isFinite(destination.lat) || !Number.isFinite(destination.lon)) {
    return { ok: false, error: 'origin/destination lat/lon must be finite numbers' };
  }
  if (!Number.isFinite(cruiseAltFt) || cruiseAltFt <= 0) {
    return { ok: false, error: 'cruiseAltFt required and positive' };
  }

  const bbox = corridorBbox(origin, destination, corridorHalfNm);

  // 1. Preload Terrarium tiles covering the corridor.
  onProgress?.('preload');
  try {
    await preloadTerrainBbox(bbox.minLat, bbox.minLon, bbox.maxLat, bbox.maxLon, undefined, signal);
  } catch (e) {
    return { ok: false, error: `tile preload failed: ${(e as Error)?.message ?? String(e)}` };
  }
  if (signal?.aborted) return { ok: false, error: 'aborted' };

  // 2. Build the packed elevation grid.
  onProgress?.('compute', 'building elevation grid');
  const grid = await buildElevationGrid(
    bbox.minLat, bbox.minLon, bbox.maxLat, bbox.maxLon,
    undefined, GRID_STEP_NM, signal,
  );
  if (signal?.aborted) return { ok: false, error: 'aborted' };

  // 3. Post to worker.
  onProgress?.('compute', 'A* search');
  const req: CanyonRouterRequest = {
    origin,
    destination,
    cruiseAltFt,
    clearanceFt,
    corridorHalfNm,
    coarseStepNm,
    refineStepNm,
    refineBandHalfNm,
    elevationGrid: {
      minLat: grid.minLat,
      maxLat: grid.maxLat,
      minLon: grid.minLon,
      maxLon: grid.maxLon,
      cols: grid.cols,
      rows: grid.rows,
      data: grid.data,
    },
  };

  return await new Promise<CanyonRouterResponse | CanyonRouterError>((resolve) => {
    if (_workerInFlight) {
      // Serialize: previous request still pending. Wait for it.
      const prev = _workerInFlight;
      _workerInFlight = (m) => { prev(m); finalDispatch(); };
    } else {
      finalDispatch();
    }
    function finalDispatch() {
      const w = getWorker();
      _workerInFlight = resolve;
      // Transfer the elevation grid buffer to avoid copying ~MB of data.
      try {
        w.postMessage(req, [req.elevationGrid.data.buffer]);
      } catch (e) {
        _workerInFlight = null;
        resolve({ ok: false, error: `worker postMessage failed: ${(e as Error)?.message ?? String(e)}` });
      }
    }
    signal?.addEventListener('abort', () => {
      if (_workerInFlight === resolve) {
        _workerInFlight = null;
        // Tear the worker down; next call will spin a fresh one.
        try { _worker?.terminate(); } catch { /* noop */ }
        _worker = null;
        resolve({ ok: false, error: 'aborted' });
      }
    }, { once: true });
  });
}
